/**
 * Личные настройки оболочки: тема, плотность, модель правки (§3, меню аватара).
 *
 * Хранятся в localStorage и выставляются атрибутами на <html> — ровно так,
 * как этого ждут токены: data-theme="dark|light" и data-density="compact".
 * Применяются до первого рендера (см. main.tsx), чтобы не было вспышки темы.
 */

export type ThemePref = 'system' | 'light' | 'dark'
export type DensityPref = 'normal' | 'compact'

export interface Prefs {
  theme: ThemePref
  density: DensityPref
}

const STORAGE_KEY = 'bm.prefs'

/* Светлая тема по умолчанию: в тёмной карточки и границы читаются тяжелее,
   а инструмент открывают днём в рабочем окне. Тёмная остаётся в настройках. */
const DEFAULTS: Prefs = { theme: 'light', density: 'normal' }

const THEMES: readonly ThemePref[] = ['system', 'light', 'dark']
const DENSITIES: readonly DensityPref[] = ['normal', 'compact']

let current: Prefs = DEFAULTS
const listeners = new Set<() => void>()

function read(): Prefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULTS
    const parsed = JSON.parse(raw) as Partial<Prefs> | null
    if (!parsed || typeof parsed !== 'object') return DEFAULTS
    return {
      theme: THEMES.includes(parsed.theme as ThemePref) ? (parsed.theme as ThemePref) : DEFAULTS.theme,
      density: DENSITIES.includes(parsed.density as DensityPref)
        ? (parsed.density as DensityPref)
        : DEFAULTS.density,
    }
  } catch {
    // Приватный режим или запрет на запись — работаем на значениях по умолчанию.
    return DEFAULTS
  }
}

function write(prefs: Prefs): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs))
  } catch {
    /* не критично: настройки живут до конца сессии */
  }
}

function apply(prefs: Prefs): void {
  const root = document.documentElement
  // 'system' — отсутствие атрибута: тогда работает @media prefers-color-scheme.
  if (prefs.theme === 'system') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', prefs.theme)

  if (prefs.density === 'normal') root.removeAttribute('data-density')
  else root.setAttribute('data-density', prefs.density)
}

/** Вызывается один раз до рендера: читает сохранённое и красит документ. */
export function applyStoredPrefs(): void {
  current = read()
  apply(current)
}

export function getPrefs(): Prefs {
  return current
}

export function setPref<K extends keyof Prefs>(key: K, value: Prefs[K]): void {
  if (current[key] === value) return
  current = { ...current, [key]: value }
  apply(current)
  write(current)
  for (const listener of listeners) listener()
}

export function subscribePrefs(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}
